import {Component, EventEmitter, OnDestroy, OnInit, Output, QueryList, ViewChildren} from '@angular/core';
import {MatExpansionPanel} from '@angular/material/expansion';
import {filter, Subject, takeUntil} from 'rxjs';
import Icons from 'src/app/core/enums/icons.enum';
import {SidenavMenuStates} from 'src/app/core/enums/sidenav-menu-states.enum';
import {SelectedMenu} from 'src/app/core/models/selected-menu.model';
import {PortalStateService} from 'src/app/core/services/portal-state.service';
import {ToolbarService} from 'src/app/core/services/toolbar.service';
import {Application} from 'src/portaljs/constants/application';
import {MessageType} from 'src/portaljs/constants/message-type';
import {MenuItem} from 'src/portaljs/models/menu-item';
import {Message} from 'src/portaljs/models/message';
import {NewMenuItemComponent} from './menu.component';
import {IconsService} from 'src/app/core/services/icons.service';
import {ScreenStateService} from 'src/app/core/services/screen-state.service';
import {ScreenState} from 'src/app/core/enums/sceen-state.enum';
import * as _ from 'lodash-es';

@Component({
  selector: 'fss-portal-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: false
})
export class SidenavComponent implements OnInit, OnDestroy {
  private readonly ngUnsubscribe = new Subject<void>();
  public Icons = Icons;
  public menuState: SidenavMenuStates;
  public SidenavMenuStates = SidenavMenuStates;
  public expandedAccordions: string[] = [];
  private activeApp: Application;
  public resetButton: {name: string, id: string, accessKey?: string};

  @Output() drawerSizeChange = new EventEmitter<void>();
  @ViewChildren(NewMenuItemComponent) matMenus: QueryList<NewMenuItemComponent>;
  @ViewChildren(MatExpansionPanel) matExpansionPanels: QueryList<MatExpansionPanel>;

  public get selectedPortal() {
    return this.toolbarService.portal;
  }

  public menus: MenuItem[] = [];
  public get menuItems() {
    return this.menus;
  }

  @ViewChildren(MatExpansionPanel)
  public accordion: QueryList<MatExpansionPanel>;

  constructor(private readonly portalStateService: PortalStateService, private readonly toolbarService: ToolbarService, public iconService: IconsService, private readonly screenStateService: ScreenStateService) {
  }

  public ngOnInit(): void {
    this.portalStateService.getActiveSecondaryToolbar$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((secondaryToolbar) => {
        //this is a HACK for LCP-392 to make the DBO secondary toolbar reset button show in the sidenav
        this.resetButton = undefined;
        if (this.activeApp === Application.DESTINY || this.activeApp === Application.DESTINY_UI) {
          const buttonItems = secondaryToolbar?.buttonItems;
          if (buttonItems && buttonItems.length > 0) {
            this.resetButton = buttonItems.find(item => item.name.includes('Reset'));
          }
        }
      });


    this.screenStateService.screenState$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((screenState) => {
        if (screenState !== ScreenState.Mobile) {
          this.portalStateService.setMenuStateForApp(this.portalStateService.getDefaultMenuStateForApp());
        }
        if (screenState === ScreenState.Mobile) {
          this.portalStateService.setMenuStateForApp(SidenavMenuStates.HIDDEN);
        }
      });

    //activateMenuItem
    this.portalStateService.getSelectedMenu$()
      .pipe(
        filter((selectedMenu: SelectedMenu) => !!selectedMenu),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((selectedMenu: SelectedMenu) => {
        const selectedMenuForApp = selectedMenu[this.portalStateService.getActiveApp().app];
        const path = this.findPath(this.menuItems, selectedMenuForApp?.menuItemId);
        if (path && path.length > 0) {
          if (!selectedMenuForApp.setByApp && !_.isEqual(this.toolbarService.selectedPath, path)) {
            this.clickButton(selectedMenuForApp?.menuItemId);
          } else {
            const ancestors = this.getAncestorsOfMenuItem(this.menuItems, selectedMenuForApp?.menuItemId);
            this.expandedAccordions = [...new Set([selectedMenuForApp?.menuItemId, ...path.slice(0, -1)])];
            for (const ancestor of ancestors) {
              this.portalStateService.setLastSelectedDescendantOfExpandableMenuItems(ancestor.id, selectedMenuForApp?.menuItemId);
            }
          }
          this.toolbarService.selectedPath = path;
        }
      });


    //registerMenuItem
    this.portalStateService.getActiveMenuItems$()
      .pipe( takeUntil(this.ngUnsubscribe) )
      .subscribe((menuItems: MenuItem[]) => {
        if (menuItems?.length === 0) {
          this.toolbarService.selectedPath = [];
        }

        if (!_.isEqual(this.menus, menuItems)) {
          const focusedElement = document.activeElement;
          this.menus = menuItems;

          //setting this.menus will trigger a re-render of the menu items, so we need to re-focus
          setTimeout(() => {
            const elem = document.getElementById('menu-' + this.toolbarService.getSelectedMenuFromPath());
            if(elem && focusedElement.id === elem.id) {
              elem.focus();
            } else if (focusedElement.id.startsWith('menu-') && focusedElement instanceof HTMLElement) {
              document.getElementById(focusedElement.id)?.focus();
            }
          });
        }


        function handleDefaultMenuItem(menuItems: MenuItem[]): MenuItem {
          for (const menuItem of menuItems) {
            if (menuItem.default) {
              return menuItem;
            }
            if (menuItem.subMenuItems && menuItem.subMenuItems.length > 0) {
              const found: MenuItem = handleDefaultMenuItem(menuItem.subMenuItems);
              if (found) {
                return found;
              }
            }
          }
          return undefined;
        }


        let findDefault = false;
        const selectedMenuId = this.portalStateService.getSelectedMenuId(this.portalStateService.getActiveApp().app);
        if (selectedMenuId) { // Don's use menuItems default if the selectedMenuId is in the menuItems
          if (menuItems?.length > 0 ) {
            findDefault = !menuItems.some(i => i.id === selectedMenuId); // use menuitems default if the selectedMenuId is not in the menuItems
          }
        }

        if (findDefault || !selectedMenuId) {
          const defaultMenuItem = handleDefaultMenuItem(menuItems);
          if (defaultMenuItem) {

            const path = this.findPath(menuItems, defaultMenuItem.id);
            if ( !_.isEqual(this.toolbarService.selectedPath, path) ) {
              this.toolbarService.selectedPath = path;
              // Update the ancestors with the last selected child
              this.saveSelectedId(defaultMenuItem.id);

              if (defaultMenuItem.subMenuItems && defaultMenuItem.subMenuItems.length > 0) {
                this.addExpanded(defaultMenuItem.id);
              } else {
                if(!this.portalStateService.clickFromAppMenu && this.portalStateService.getActiveApp().app !== Application.DESTINY_UI ){
                  //LCP-488 if Destiny UI, don't auto-click the default menu item (which is to DBO)
                  this.clickButton(defaultMenuItem.id, 'clicked');
                }
              }
              this.portalStateService.clickFromAppMenu = false;
            }
          }
        }
      });

    this.portalStateService.getActiveApp$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((activeApp) => {
        this.activeApp = activeApp.app;
        this.expandedAccordions = [];
      });


    this.portalStateService.getMenuState$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state) => {
        const menuState = state[this.activeApp];

        if (menuState?.current !== SidenavMenuStates.STANDARD) {
          if (this.matExpansionPanels) {
            this.matExpansionPanels.forEach((panel) => {
              panel.close();
            });
          }
        }

        this.menuState = menuState?.current || (this.isMobile() ? SidenavMenuStates.HIDDEN : SidenavMenuStates.STANDARD);
        if (!this.isMobile()) {
          //material doesn't like when you change the width of the drawer while it's open, so we manually handle that in portal component
          //on mobile, the drawer does't change width, it just opens and closes, so it is handled automatically
          setTimeout(() => this.drawerSizeChange.emit());
        }
      });
    }




  public getIsDestinyViewSelectorEnabled(): boolean {
    return this.portalStateService.getIsDestinyViewSelectorEnabled();
  }

  public isMobile() {
    return this.toolbarService.isMobile();
  }


  public focusTrigger(id: string) {
    const elem = document.getElementById('menu-' + id);
    if (elem) {
      elem.focus();
    }
  }

  public getResetButtonText(): string {
    if (this.toolbarService.selectedPath.some(item => item.toLowerCase().includes('checkout'))) {
      return 'Reset Checkout';
    }
    if (this.toolbarService.selectedPath.some(item => item.toLowerCase().includes('checkin'))) {
      return 'Reset Checkin';
    }
    return this.resetButton ? this.resetButton.name : '';
  }

  public isExpanded(id: string) {
    return this.expandedAccordions.includes(id);
  }

  public clickResetButton() {
    const id = this.resetButton.id;
    const msg = new Message(id, MessageType.secondaryToolbarSelect, Application.PORTAL, this.portalStateService.getActiveApp().app, 'clicked');
    this.portalStateService.postMessage(msg);
  }

  public findPath(data: MenuItem[], targetId: string): string[] {
    function search(arr: MenuItem[], targetId: string, path: string[]): any {
      for (const obj of arr) {
        if (obj.id === targetId) {
          return [...path, obj.id];
        }
        if (obj.subMenuItems && obj.subMenuItems.length > 0) {
          const result = search(obj.subMenuItems, targetId, [...path, obj.id]);
          if (result) {
            return result;
          }
        }
      }
      return null;
    }
    return search(data, targetId, []);
  }

  public openHelpLink() {
    window.open('https://follettsoftware.com/', '_blank');
  }

  public getMatMenu(index: string) {
    if (this.matMenus && this.menuState !== SidenavMenuStates.STANDARD) {
      const menuComponent = this.matMenus.find(menu => menu.menu.id === index);
      return menuComponent ? menuComponent.matmenus.first : null;
    }
    return undefined;
  }

  public handleMatMenuTriggerKeydown(button: HTMLButtonElement) {
    button.click();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public isHighlighted(id: string) {
    return this.toolbarService.isHighlighted(id);
  }

  public childHasCount(id: string): any {
    function hasCountGreaterThanZero(obj: MenuItem): boolean {
      if (obj && obj.count > 0) {
        return true;
      }

      if (obj && obj.subMenuItems && Array.isArray(obj.subMenuItems)) {
        for (const child of obj.subMenuItems) {
          if (hasCountGreaterThanZero(child)) {
            return true;
          }
        }
      }
      return false;
    }
    return hasCountGreaterThanZero(this.menuItems.find(x => x.id === id));
  }

  public closeAccordion(): void {
    if (this.accordion && this.accordion.length > 0) {
      this.accordion.forEach((matExpansion) => matExpansion.close());
    }
  }

  public login(): void {
    this.toolbarService.login();
  }

  public closeMobileMenu() {
    this.closeAccordion();
  }


  public saveSelectedId(itemId: string): void {
    const selectedMenu: SelectedMenu = { [this.portalStateService.getActiveApp().app]: { menuItemId: itemId } };
    this.portalStateService.setSelectedMenuId(selectedMenu);
  }


  public isDDorCollections() {
    return this.portalStateService.getActiveApp().app === Application.DD || this.portalStateService.getActiveApp().app === Application.COLLECTIONS;
  }

  private findMenuItemById(menuItems: MenuItem[], selectedId: string): MenuItem | undefined {
    for (const item of menuItems) {
      if (item.id === selectedId) {
        return item;
      }
      if (item.subMenuItems) {
        const found = this.findMenuItemById(item.subMenuItems, selectedId);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }


  private getParentOfMenuItem(menuItems: MenuItem[], id: string): MenuItem | undefined {
    for (const item of menuItems) {
      if (item.subMenuItems && item.subMenuItems.length > 0) {
        if (item.subMenuItems.some(subItem => subItem.id === id)) {
          return item;
        }
        const parent = this.getParentOfMenuItem(item.subMenuItems, id);
        if (parent) {
          return parent;
        }
      }
    }
    return undefined;
  }

  private getAncestorsOfMenuItem(menuItems: MenuItem[], id: string): MenuItem[] {
    const ancestors: MenuItem[] = [];
    function findAncestors(items: MenuItem[], targetId: string): boolean {
      for (const item of items) {
        if (item.id === targetId) {
          return true;
        }
        if (item.subMenuItems && item.subMenuItems.length > 0) {
          if (findAncestors(item.subMenuItems, targetId)) {
            ancestors.push(item);
            return true;
          }
        }
      }
      return false;
    }
    findAncestors(menuItems, id);
    return ancestors;
  }

  private getDescendantIds(item: MenuItem): string[] {
    const ids: string[] = [];
    if (item.subMenuItems) {
      for (const child of item.subMenuItems) {
        ids.push(child.id, ...this.getDescendantIds(child));
      }
    }
    return ids;
  }


  // This is only called for menu items that are expandable
  public addExpanded(id: string) {
    const menuItem = this.findMenuItemById(this.menuItems, id);
    if (this.isExpanded(id)) {
      // Collapse me and all my descendants
      const allDescendants = menuItem ? this.getDescendantIds(menuItem) : [];
      this.expandedAccordions = this.expandedAccordions.filter(expId => expId !== id && !allDescendants.includes(expId));
    } else {
      // Collapse everything but me and my ancestors
      const parent = this.getParentOfMenuItem(this.menuItems, id);
      if (parent && parent.subMenuItems) {
        this.expandedAccordions = this.expandedAccordions.filter(expId => !parent.subMenuItems.some(sib => sib.id === expId));
      } else {
        this.expandedAccordions = this.expandedAccordions.filter(expId => expId === id);
      }
      if (_.isEmpty(menuItem?.subMenuItems)) { // No submenus...
        const ancestors = this.getAncestorsOfMenuItem(this.menuItems, id);
        this.expandedAccordions = [...new Set([id, ...ancestors.map(a => a.id)])];
        this.clickButton(id);
      } else { // Submenus...
        const previouslySelectedDescendant = this.portalStateService.getLastSelectedDescendantOfExpandableMenuItems(id);
        if (previouslySelectedDescendant) {
          // I have a lastSelectedDescendant. Expand to and select the lastSelectedDescendant
          const path = this.findPath(this.menuItems, previouslySelectedDescendant);
          this.expandedAccordions = [...new Set([id, ...path.slice(0, -1)])];
          this.clickButton(previouslySelectedDescendant);
        } else {
          const ancestors = this.getAncestorsOfMenuItem(this.menuItems, id);
          const firstDescendant = menuItem.subMenuItems[0];

          // Check if firstDescendant has a lastSelectedDescendant
          const lastSelectedDescendant = this.portalStateService.getLastSelectedDescendantOfExpandableMenuItems(firstDescendant.id);
          if (lastSelectedDescendant) {
            // Expand all ancestors of the last selected descendant of the firstDecendant and select it
            const path = this.findPath(this.menuItems, lastSelectedDescendant);
            this.expandedAccordions = [...new Set([id, ...path.slice(0, -1)])];
            this.clickButton(lastSelectedDescendant);
          } else {
            // No last selected firstDecentent: select first child of firstDecendant
            if (firstDescendant.subMenuItems && firstDescendant.subMenuItems.length > 0) {
              const firstGrandchild = firstDescendant.subMenuItems[0];
              const path = this.findPath(this.menuItems, firstGrandchild.id);
              this.expandedAccordions = [...new Set([id, ...path.slice(0, -1)])];
              this.clickButton(firstGrandchild.id);
            } else {
              // Submenuitem has no children: select the submenuitem
              this.expandedAccordions = [...new Set([id, ...ancestors.map(a => a.id), firstDescendant.id])];
              this.clickButton(firstDescendant.id);
            }
          }
        }
      }
    }
  }


  public clickButton(selectedId: string, overrideMessage?: string): void {
    const portalApp = this.portalStateService.getActiveApp();
    let menuApp: Application = portalApp.app;
    if (portalApp && portalApp.parent) { // If this is a child app, send the click event to the parent app instead.
      const portalApp = this.portalStateService.getSelectableApp(menuApp);
      menuApp = portalApp.parent;
    }

    const msg = new Message(selectedId, MessageType.menuItemSelect, Application.PORTAL, menuApp, overrideMessage? overrideMessage : 'clicked');
    this.portalStateService.postMessage(msg);

    if (this.isMobile()) {
      this.toolbarService.toggleSidenav();
    }

    const menuItem = this.findMenuItemById(this.menuItems, selectedId);
    if (menuItem && menuItem.externalUrl) {
      window.open(menuItem.externalUrl, '_blank');
    } else if (!menuItem.externalUrl) {
      const path = this.findPath(this.menuItems, selectedId);
      this.toolbarService.selectedPath = path;

      // Update the ancestors with the last selected child
      const ancestors = this.getAncestorsOfMenuItem(this.menuItems, selectedId);
      this.expandedAccordions = [...new Set([selectedId, ...path.slice(0, -1)])];
      for (const ancestor of ancestors) {
          this.portalStateService.setLastSelectedDescendantOfExpandableMenuItems(ancestor.id, selectedId);
      }
      this.saveSelectedId(selectedId);
    }
  }

}
